"use client";

import React from "react";
import Image from "next/image";
import { PortableText } from "@portabletext/react";

import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import DotsBackdrop from "@/components/lp-long/DotsBackdrop";
import { cn } from "@/lib/utils";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { PageLPLongSchema } from "@/sanity/pages/PageLPLongTemplate";

import check from "@/public/lp-long/audience-check.svg";

export default function AudienceSection({
  data,
  className,
}: {
  data: PageLPLongSchema;
  className?: string;
}) {
  const items = data?.audienceItems ?? [];

  return (
    <section
      id="audience"
      className={cn(
        "relative overflow-clip bg-[#0A1A1D] py-[8rem] lg:py-[14.625rem]",
        className,
      )}
    >
      <DotsBackdrop light className="hidden lg:flex" />
      {/* side lines: 170px from the edge on a 1920 canvas */}
      <DecorationLine
        className="hidden lg:block lg:left-[8.85%]"
        orientation="vertical"
        position="left"
        style="light"
      />
      <DecorationLine
        className="hidden lg:block lg:right-[8.85%]"
        orientation="vertical"
        position="right"
        style="light"
      />

      <div className="container relative flex flex-col items-center">
        <Chip className="mb-[1rem]" image={data?.audienceChip?.icon} text={data?.audienceChip?.text} />

        {data?.audienceTitle && (
          <div className="richtext prose-headings:mb-0 prose-headings:w-[min(71rem,100%)] prose-headings:text-[4rem] prose-headings:leading-none prose-headings:text-white prose-em:font-ivy prose-em:text-[#D9F9B9] lg:prose-headings:text-[6.5rem]">
            <PortableText value={data.audienceTitle} components={richTextComponent} />
          </div>
        )}

        {!!items.length && (
          <ul className="mt-[4rem] grid w-[min(150rem,100%)] grid-cols-1 gap-[1.25rem] lg:mt-[5.5rem] lg:grid-cols-3">
            {items.map((item, i) => (
              <li
                key={i}
                className="flex flex-col gap-[8rem] border-t-[3px] border-[#D9F9B9] bg-[#112628] p-[4rem]"
              >
                <span className="flex h-[6rem] w-[6rem] items-center justify-center bg-[#D9F9B9]/10">
                  {item?.icon && <Image className="!w-[4rem]" src={item.icon} alt="" />}
                </span>

                <div className="flex flex-col gap-[3.25rem]">
                  <p className="mb-0 font-geist text-[2.75rem] font-medium leading-[1.4] text-white lg:text-[3.5rem] [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]">
                    {item?.title}
                  </p>
                  {!!item?.points?.length && (
                    <ul className="flex flex-col gap-[3rem]">
                      {item.points.map((point, p) => (
                        <li key={p} className="flex items-center gap-[1rem]">
                          <Image className="!w-[1.5rem] shrink-0" src={check} alt="" />
                          <p className="mb-0 text-[2.25rem] leading-[1.5] text-white/60 lg:whitespace-nowrap [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]">{point}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {data?.audienceCTA && (
          <CTACornerButton className="mt-[4rem] lg:mt-[6.625rem]" data={data.audienceCTA} />
        )}
      </div>
    </section>
  );
}
